import { useEffect, useState } from 'react';
import { Box, ListItemIcon, ListItemText, MenuItem } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Paper, GridColumnProps } from '@roc/ui';
import { observer } from 'mobx-react';
import { ProtectiveAdvancesGrid } from '../protectiveAdvancesGrid';
import { getPayableReceivablesStatusOptions, PayableReceivablesTypes, RECEIVABLES_TABS, TYPES_OF_FEE } from '../../types/types';
import { useReceivablesDashboardStore } from '../../hooks/useReceivablesDasboardStore';
import { filterProps, noOpComparator, selectSearchFilterProps, selectFilterProps, dateFilterProps } from '@roc/ui/utils';
import { ReceivablesToolbar } from './receivablesToolbar';
import { useDocumentStore } from '@roc/feature-documents';
import { CloudDownload, History, Tune } from '@material-ui/icons';
import { HistoryModal } from '../historyModal';
import { MakeAnAdjustmentModal } from '../makeAnAdjustmentModal';
import { usePayablesDashboardStore } from '../../hooks/usePayablesDashboardStore';
import { LOAN_STATUS_OPTIONS, LOAN_SUB_TYPE_OPTIONS } from '../../utils/constants';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    //Same as nportal dashboard
    maxWidth: '1400px',
  },
  paper: {
    boxShadow: 'none !important',
    border: '1px solid #cdcdcd',
    padding: '0px',
    borderRadius: '0 !important',
  },
}));



export const ReceivablesDashboard = observer(() => {
  const classes = useStyles();
  const { receivablesDashboardStore } = useReceivablesDashboardStore();
  const { payablesApprovedStore: { vendorOptions } } = usePayablesDashboardStore();
  const { tab } = receivablesDashboardStore;
  const { documentStore } = useDocumentStore();
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [actionSelectedRecord, setActionSelectedRecord] = useState<any>(null);

  useEffect(() => {
    receivablesDashboardStore.fetchEntityNames();
  }, []);

  const actions = (record, handleMenuClose) => {

    return [
      <MenuItem
        disabled={!record.documentId}
        onClick={() => {
          const document = {
            taskId: record.documentId,
            originalFileName: record.originalFileName,
          };
          documentStore.downloadDocumentOriginalNameById(document);
          handleMenuClose();
        }}
      >
        <ListItemIcon>
          <CloudDownload fontSize="small" />
        </ListItemIcon>
        <ListItemText>Download Invoice</ListItemText>
      </MenuItem>,
      <MenuItem
        onClick={() => {
          setActionSelectedRecord(record);
          setShowAdjustmentModal(true);
          handleMenuClose();
        }}
        disabled={
          record.status === PayableReceivablesTypes.REJECTED ||
          record.status === PayableReceivablesTypes.PAID ||
          record.status === PayableReceivablesTypes.PROCESSING
        }
      >
        <ListItemIcon>
          <Tune fontSize="small" />
        </ListItemIcon>
        <ListItemText>Make an Adjustment</ListItemText>
      </MenuItem>,
      <MenuItem
        onClick={() => {
          receivablesDashboardStore.openHistoryModal(record);
          handleMenuClose();
        }}
      >
        <ListItemIcon>
          <History fontSize="small" />
        </ListItemIcon>
        <ListItemText>View History</ListItemText>
      </MenuItem>
    ];
  }

  const RECEIVABLES_COLUMNS: GridColumnProps[] = [

    {
      field: 'loanId',
      headerName: 'Loan Id',
      width: 90,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'loanStatus',
      headerName: 'Loan Status',
      minWidth: 125,
      comparator: noOpComparator,
      cellRenderer: 'loanStatusCellRenderer',
      ...selectFilterProps(LOAN_STATUS_OPTIONS),
    },
    {
      field: 'loanSubType',
      headerName: 'Loan Sub-type',
      minWidth: 135,
      comparator: noOpComparator,
      ...selectFilterProps(LOAN_SUB_TYPE_OPTIONS),
    },
    {
      field: 'payoffDate',
      headerName: 'Payoff Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'entity',
      headerName: 'Investor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => receivablesDashboardStore.investorOptions)
    },
    {
      field: 'vendorName',
      headerName: 'Vendor',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(null, () => vendorOptions),
    },
    {
      field: 'invoice',
      headerName: 'Invoice #',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'invoiceDate',
      headerName: 'Invoice Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'transactionNumber',
      headerName: 'Transaction Id',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'feeType',
      headerName: 'Type',
      minWidth: 125,
      comparator: noOpComparator,
      ...selectSearchFilterProps(TYPES_OF_FEE, null)
    },
    {
      field: 'payableStatus',
      headerName: 'Payable Status',
      minWidth: 150,
      comparator: noOpComparator,
      cellRenderer: 'statusCellRenderer',
      ...selectFilterProps(getPayableReceivablesStatusOptions(tab)),
    },
    {
      field: 'status',
      headerName: 'Receivable Status',
      minWidth: 150,
      comparator: noOpComparator,
      ...selectFilterProps(getPayableReceivablesStatusOptions(tab)),
      editable: true,
      cellEditor: 'dropdownGridEditor',
      cellRenderer: 'statusCellRenderer',
    },
    {
      field: 'vendorPaidDate',
      headerName: 'Vendor Paid Date',
      minWidth: 140,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'investorBillId',
      headerName: 'Investor Bill ID',
      minWidth: 135,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'investorBillDate',
      headerName: ' Investor Bill Date',
      minWidth: 140,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'originalAmount',
      headerName: 'Original Amount',
      minWidth: 135,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'adjustment',
      headerName: 'Receivable Adjustment',
      minWidth: 135,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'amount',
      headerName: 'Investor Amount Due',
      minWidth: 140,
      cellRenderer: 'currencyCellRenderer',
      comparator: noOpComparator,
      ...filterProps(),
    },
    {
      field: 'investorPaidDate',
      headerName: 'Investor Paid Date',
      minWidth: 145,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'receivableAgeing',
      headerName: 'Receivable Ageing',
      minWidth: 145,
      comparator: noOpComparator,
      ...filterProps()
    },
    {
      field: 'createdDate',
      headerName: 'Creation Date',
      minWidth: 125,
      cellRenderer: 'dateCellRenderer',
      comparator: noOpComparator,
      ...dateFilterProps(),
    },
    {
      field: 'comment',
      headerName: 'Comments',
      minWidth: 125,
      comparator: noOpComparator,
      ...filterProps(),
      editable: true,
      cellEditor: 'commentEditor',
      cellEditorParams: {
        type: tab,
      },
    },
    {
      field: 'leadId',
      headerName: 'Action',
      minWidth: 80,
      cellRenderer: 'actionCellRenderer',
      comparator: noOpComparator,
      width: 80,
      pinned: 'right',
      lockPinned: true,
    },
  ];

  return (
    <>
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex={1}>
          <Paper className={classes.paper}>
            <ProtectiveAdvancesGrid
              counterPartyExpenseStore={receivablesDashboardStore}
              columns={RECEIVABLES_COLUMNS}
              selectModeEnabled={true}
              toolbar={
                <ReceivablesToolbar
                  tabs={RECEIVABLES_TABS}
                  receivablesDashboardStore={receivablesDashboardStore}
                />}
              actions={actions}
            />
          </Paper>
        </Box>
      </Box>
      <MakeAnAdjustmentModal
        open={showAdjustmentModal}
        record={actionSelectedRecord}
        onClose={() => setShowAdjustmentModal(false)}
        onSubmit={async (amount, notes) => {
          await receivablesDashboardStore.updateReceivableAdjustment(actionSelectedRecord.recordId, amount, notes);
          receivablesDashboardStore.counterPartyExpenseGridStore.refresh();
        }}
      />
      <HistoryModal
        open={receivablesDashboardStore.historyModalOpen}
        history={receivablesDashboardStore.receivableHistory}
        isLoading={receivablesDashboardStore.historyLoading}
        onClose={() => receivablesDashboardStore.closeHistoryModal()}
      />
    </>

  );
});
